import { Stack, TableRow } from '@mui/material';
import React, { memo } from 'react';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import CusLink from '../../../components/material-ui/CusLink';
import { useAppSelector } from '../../../store';
import { useTranslation } from 'react-i18next';
import CurrencyValue from '../../../components/material-ui/CurrencyValue';
import CusSiteDateTime from '../../../components/material-ui/CusSiteDateTime';

const RefcodeTableRow = memo(
  ({ refcode, refcodeIndx, setHoverIndex, isHoverIndx }) => {
    const { t } = useTranslation();
    const groupHash = useAppSelector((state) => state.user.group.hash);
    const refcodeCurrency = useAppSelector((state) => state.reports.refcode.payload?.currency);

    const getGroupName = (id) => {
      return groupHash[id]?.name || id;
    };

    const MyTableCurrency = ({ value }) => (
      <MyTableCell isLess type="numeric">
        <CurrencyValue
          fontSize={12}
          currency={refcodeCurrency}
          value={value}
          defaultValue="0"
          justifyContent={'end'}
        />
      </MyTableCell>
    );

    const getReducedSum = (indx) => refcode?.data?.reduce((prev, el) => el[indx] + prev, 0);

    const rowBg = isHoverIndx ? 'action.hover' : 'transparent';

    const countCommonDep = getReducedSum(1);
    const countFirstDep = getReducedSum(2);
    const countRepDep = getReducedSum(3);
    const countReg = getReducedSum(4);
    const countFirstReg = getReducedSum(5);
    const totalFirstReg = getReducedSum(6);
    const totalFirstDep = getReducedSum(7);
    const totalReptDep = getReducedSum(8);
    const totalDep = getReducedSum(9);
    const averageDep = getReducedSum(10);

    const RefcodeRowContent = ({ refcode }) => {
      if (refcode[0] == 0) {
        return (
          <TableRow
            onMouseEnter={() => setHoverIndex(refcodeIndx)}
            onMouseLeave={() => setHoverIndex(null)}
            sx={{ backgroundColor: rowBg }}>
            <MyTableCell colSpan={11} isSmall>
              <Stack px={1} direction={'row'} alignItems={'center'} gap={1}>
                <TypographyText>
                  {t('Group')}: <CusLink text={refcode[4]} to={`/groups/${refcode[3]}`} />,
                </TypographyText>
                <TypographyText>
                  {t('Ref code')}:{' '}
                  <TypographyText isCopy={refcode[1]} sx={{ p: 0.5, px: 1 }}>
                    {refcode[1]}
                  </TypographyText>
                  ,
                </TypographyText>
                <TypographyText>
                  {t('Comment')}: {refcode[2]},
                </TypographyText>
              </Stack>
            </MyTableCell>
          </TableRow>
        );
      }

      if (refcode[0] == 1) {
        return (
          <TableRow
            sx={{ backgroundColor: rowBg }}
            onMouseEnter={() => setHoverIndex(refcodeIndx)}
            onMouseLeave={() => setHoverIndex(null)}>
            <MyTableCell isLess sx={{ width: 90 }}>
              {<CusSiteDateTime date={refcode[1]} format="DD.MM.YYYY" />}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[2]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[3]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[7]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[4]}
            </MyTableCell>
            <MyTableCurrency value={refcode[9]} />
            <MyTableCurrency value={refcode[10]} />
            <MyTableCurrency value={refcode[5]} />
            <MyTableCurrency value={refcode[8]} />
            <MyTableCurrency value={refcode[6]} />
            <MyTableCurrency value={refcode[2] ? refcode[6] / refcode[2] : 0} />
          </TableRow>
        );
      }

      if (refcode[0] == 2) {
        return (
          <TableRow
            sx={{
              backgroundColor: 'action.selected'
            }}
            onMouseEnter={() => setHoverIndex(refcodeIndx)}
            onMouseLeave={() => setHoverIndex(null)}>
            <MyTableCell isLess>
              {t('Total')}: {refcode[1]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[2]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[3]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[7]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[4]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[9]}
            </MyTableCell>
            <MyTableCell isLess type="numeric">
              {refcode[10]}
            </MyTableCell>
            <MyTableCurrency value={refcode[5]} />
            <MyTableCurrency value={refcode[8]} />
            <MyTableCurrency value={refcode[6]} />
            <MyTableCurrency value={refcode[2] ? refcode[6] / refcode[2] : 0} />
          </TableRow>
        );
      }
    };

    return (
      <React.Fragment key={refcodeIndx}>
        <RefcodeRowContent refcode={refcode} />
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.refcode[0] === nextProps.refcode[0] &&
      prevProps.refcode[1] === nextProps.refcode[1] &&
      prevProps.refcode[2] === nextProps.refcode[2] &&
      prevProps.refcode[3] === nextProps.refcode[3] &&
      prevProps.refcode[4] === nextProps.refcode[4] &&
      prevProps.refcode[5] === nextProps.refcode[5] &&
      prevProps.refcode[6] === nextProps.refcode[6] &&
      prevProps.refcode[7] === nextProps.refcode[7] &&
      prevProps.refcode[8] === nextProps.refcode[8] &&
      prevProps.refcode[9] === nextProps.refcode[9] &&
      prevProps.refcode[10] === nextProps.refcode[10] &&
      prevProps.isHoverIndx === nextProps.isHoverIndx
    );
  }
);

RefcodeTableRow.displayName = 'RefcodeTableRow';

export default RefcodeTableRow;
